import {
  ApartmentOutlined,
  AuditOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  HomeOutlined,
  HourglassOutlined,
  IdcardOutlined,
  MailOutlined,
  MedicineBoxOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { Button, DatePicker, Form, InputNumber } from 'antd';
import dayjs from 'dayjs';
import {
  Group,
  MasterSwitch,
  SettingChoice,
  SettingSwitch,
} from '@/components/profile/settingCards';
import { cn } from '@/libs/utils';
import {
  HR_DOCUMENTS_TYPES_OPTIONS,
  SEPARATED_REQUEST_DOCS_OPTIONS,
  WORK_TYPES_OPTIONS,
} from '@/shared/company';
import { DATE_FORMATS } from '@/utils/formUtils';
import { translateOptions } from '@/utils/utils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';

// The HR tile of the company profile as setting cards. Same fields, names,
// visibility rules and save as CompanyHrSettings + CompanyHrRulesFields + the
// work-from-home radio, which step aside on the profile.

export default function CompanyHrProfilePanel() {
  const ctx = useCompanyGeneralDataForm();
  const {
    companyAdminsReceiveHREmailsKeys,
    companyAdminsReceiveHREmailsState,
    documentReturnTypeKeys,
    documentReturnTypeState,
    documentSeparateRequestResponseKeys,
    documentSeparateRequestResponseState,
    filteredHrDocumentsOptions,
    foreignWorkPermitTrackerKeys,
    foreignWorkPermitTrackerState,
    holidayDaysTrackerChecked,
    holidayDaysTrackerKeys,
    holidayDaysTrackerState,
    isEdited,
    isForEmployeesKeys,
    isForEmployeesState,
    isHrChecked,
    isHrKeys,
    isHrOfficerSelfApproveKeys,
    isHrOfficerSelfApproveState,
    isHrState,
    isSectorChecked,
    isSectorKeys,
    isSectorState,
    isSickLeaveRequestKeys,
    isSickLeaveRequestState,
    loyaltyEnabled,
    readOnly,
    sectionClass,
    shouldHrGroup,
    strictOldHolidayCutoffKeys,
    strictOldHolidayCutoffState,
    holidayFirstPartRequiredKeys,
    holidayFirstPartRequiredState,
    t,
    trackerStartDateKeys,
    trackerStartDateState,
    typeOfHrKeys,
    typeOfHrState,
    weekendLeavesKeys,
    weekendLeavesState,
    workFromHomeTypeKeys,
    workFromHomeTypeState,
  } = ctx;
  const ui = (key) => t(`companyProfile.hrUi.${key}`, { ns: 'translations' });
  const f = (key) => t(key, { ns: 'forms' });
  const m = (key) => t(key, { ns: 'messages' });
  const lock = (state) => readOnly || !!state?.disabled;

  // One setting = one Form.Item with the same name as on the old layout.
  const toggle = (keys, state, props) =>
    state?.visible && (
      <Form.Item name={keys} noStyle>
        <SettingSwitch locked={lock(state)} {...props} />
      </Form.Item>
    );
  const choice = (keys, state, props) =>
    state?.visible && (
      <Form.Item name={keys} noStyle>
        <SettingChoice locked={lock(state)} {...props} />
      </Form.Item>
    );

  const trackerDate = trackerStartDateState?.visible && (
    <div className="flex items-center gap-2 pl-[42px] text-[12px] text-zinc-500">
      {f('startDateOfWork')}
      <Form.Item
        name={trackerStartDateKeys}
        className="!mb-0 [&_.ant-form-item-control-input]:!min-h-0"
        rules={[
          { required: true, message: f('formValidation.selectDateRequired') },
        ]}
      >
        {lock(trackerStartDateState) ? (
          <DateText />
        ) : (
          <DatePicker
            size="small"
            className="w-36"
            format={DATE_FORMATS.DISPLAY}
          />
        )}
      </Form.Item>
    </div>
  );

  const loyaltyThresholds = (
    <Form.List name={['loyaltyHolidaySettings', 'thresholds']}>
      {(fields, { add, remove }) => (
        <div className="grid gap-1.5">
          {fields.map(({ key, name, ...restField }) => (
            <div key={key} className="flex items-center gap-2 text-[12.5px]">
              {m('afterYears')}
              <Form.Item
                {...restField}
                name={[name, 'yearsOfService']}
                className="!mb-0"
                rules={[{ required: true }]}
              >
                <InputNumber
                  size="small"
                  min={1}
                  max={50}
                  disabled={readOnly || !isEdited}
                  className="w-14"
                />
              </Form.Item>
              → +
              <Form.Item
                {...restField}
                name={[name, 'additionalDays']}
                className="!mb-0"
                rules={[{ required: true }]}
              >
                <InputNumber
                  size="small"
                  min={1}
                  max={30}
                  disabled={readOnly || !isEdited}
                  className="w-14"
                />
              </Form.Item>
              {m('daysLabel')}
              {!readOnly && (
                <Button
                  type="link"
                  danger
                  size="small"
                  onClick={() => remove(name)}
                  className="!p-0"
                >
                  ✕
                </Button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              type="dashed"
              size="small"
              className="w-max"
              onClick={() => add({ yearsOfService: 1, additionalDays: 1 })}
            >
              + {m('addThreshold')}
            </Button>
          )}
        </div>
      )}
    </Form.List>
  );

  const workFromHome = choice(workFromHomeTypeKeys, workFromHomeTypeState, {
    icon: <HomeOutlined />,
    title: f('workFromHome'),
    options: translateOptions(WORK_TYPES_OPTIONS, t),
  });
  // Mounted in exactly one place, so the field is never registered twice.
  const wfhInRequests = shouldHrGroup && isHrChecked;

  const languageOptions = translateOptions(filteredHrDocumentsOptions, t);
  const language = Form.useWatch(typeOfHrKeys, ctx.form);
  const languageLabel = languageOptions.find(
    (o) => o.value === language
  )?.label;

  return (
    <div className={cn('grid gap-4', sectionClass('hr'))}>
      {/* Master switch */}
      {isHrState?.visible && (
        <Form.Item name={isHrKeys} noStyle>
          <MasterSwitch
            locked={lock(isHrState)}
            title={f('hrPlatform')}
            desc={ui('hrDesc')}
            summary={isHrChecked && shouldHrGroup ? languageLabel : null}
          />
        </Form.Item>
      )}

      {/* Mounted while HR is off too, like the old block (hidden there). */}
      {shouldHrGroup && (
        <div className={cn('grid gap-4', !isHrChecked && 'hidden')}>
          <Group title={ui('groupRequests')}>
            {toggle(isForEmployeesKeys, isForEmployeesState, {
              icon: <TeamOutlined />,
              title: f('isForEmployees'),
              desc: ui('forEmployeesDesc'),
            })}
            {toggle(isSickLeaveRequestKeys, isSickLeaveRequestState, {
              icon: <MedicineBoxOutlined />,
              title: f('isSickLeaveRequest'),
              desc: ui('sickLeaveDesc'),
            })}
            {toggle(weekendLeavesKeys, weekendLeavesState, {
              icon: <CalendarOutlined />,
              title: f('weekendLeaves'),
              desc: ui('weekendDesc'),
            })}
            {isHrChecked &&
              toggle(isSectorKeys, isSectorState, {
                icon: <ApartmentOutlined />,
                title: f('sectors'),
                desc: ui('sectorsDesc'),
                wide: isSectorChecked && isHrOfficerSelfApproveState?.visible,
                children: isSectorChecked &&
                  isHrOfficerSelfApproveState?.visible && (
                    <Form.Item name={isHrOfficerSelfApproveKeys} noStyle>
                      <SettingSwitch
                        locked={lock(isHrOfficerSelfApproveState)}
                        title={f('questionnaire.hrOfficerSelfApprove')}
                        hint={f('questionnaire.hrOfficerSelfApproveInfo')}
                        icon={<ApartmentOutlined />}
                      />
                    </Form.Item>
                  ),
              })}
            {wfhInRequests && workFromHome}
          </Group>
        </div>
      )}

      {/* Not an HR setting: the old form showed it whether HR was on or not. */}
      {!wfhInRequests && workFromHome && <Group>{workFromHome}</Group>}

      {(isHrChecked || foreignWorkPermitTrackerState?.visible) && (
        <Group title={ui('groupLeave')}>
          {isHrChecked &&
            toggle(holidayDaysTrackerKeys, holidayDaysTrackerState, {
              wide: true,
              icon: <FieldTimeOutlined />,
              title: f('holidayDaysTracker'),
              desc: ui('trackerDesc'),
              children: holidayDaysTrackerChecked && trackerDate,
            })}
          {isHrChecked &&
            holidayDaysTrackerChecked &&
            toggle(strictOldHolidayCutoffKeys, strictOldHolidayCutoffState, {
              icon: <HourglassOutlined />,
              title: f('strictOldHolidayCutoff'),
              hint: f('strictOldHolidayCutoffTooltip'),
            })}
          {isHrChecked &&
            holidayDaysTrackerChecked &&
            toggle(holidayFirstPartRequiredKeys, holidayFirstPartRequiredState, {
              icon: <CalendarOutlined />,
              title: f('holidayFirstPartRequired'),
              hint: f('holidayFirstPartRequiredTooltip'),
            })}
          {isHrChecked && (
            <Form.Item name={['loyaltyHolidaySettings', 'enabled']} noStyle>
              <SettingSwitch
                locked={readOnly || !isEdited}
                wide={loyaltyEnabled}
                icon={<TrophyOutlined />}
                title={m('loyaltyHolidaySettings')}
              >
                {loyaltyEnabled && loyaltyThresholds}
              </SettingSwitch>
            </Form.Item>
          )}
          {toggle(foreignWorkPermitTrackerKeys, foreignWorkPermitTrackerState, {
            icon: <IdcardOutlined />,
            title: f('foreignWorkPermitTracker'),
            desc: ui('permitDesc'),
          })}
        </Group>
      )}

      {shouldHrGroup && (
        <div className={cn(!isHrChecked && 'hidden')}>
          <Group title={ui('groupDocs')}>
            {choice(typeOfHrKeys, typeOfHrState, {
              select: true,
              icon: <AuditOutlined />,
              title: f('hrDocuments'),
              hint: m('hrDocumentsInfoMessage'),
              options: languageOptions,
            })}
            {choice(documentReturnTypeKeys, documentReturnTypeState, {
              icon: <AuditOutlined />,
              title: f('hrDocumentsTypes'),
              hint: m('hrDocumentsFormatInfoMessage'),
              options: translateOptions(HR_DOCUMENTS_TYPES_OPTIONS, t),
            })}
            {choice(
              documentSeparateRequestResponseKeys,
              documentSeparateRequestResponseState,
              {
                icon: <AuditOutlined />,
                title: f('separatedRequestDocs'),
                hint: m('separatedRequestDocsInfoMessage'),
                options: translateOptions(SEPARATED_REQUEST_DOCS_OPTIONS, t),
              }
            )}
            {toggle(
              companyAdminsReceiveHREmailsKeys,
              companyAdminsReceiveHREmailsState,
              {
                wide: true,
                icon: <MailOutlined />,
                title: f('companyAdminReceiveHREmails'),
                desc: ui('adminsEmailsDesc'),
              }
            )}
          </Group>
        </div>
      )}
    </div>
  );
}

const DateText = ({ value }) => (
  <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-bold text-zinc-800">
    {value ? dayjs(value).format(DATE_FORMATS.DISPLAY) : '—'}
  </span>
);
